import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import TopBar from './components/layout/TopBar';
import BottomNav from './components/layout/BottomNav';
import Dashboard from './pages/Dashboard';
import Students from './pages/Students';
import Attendance from './pages/Attendance';
import Fees from './pages/Fees';
import Profile from './pages/Profile';
import Contact from './pages/Contact';
import PaymentHistory from './pages/PaymentHistory';
import Login from './pages/Login';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import ResetPasswordForm from './components/auth/ResetPasswordForm';
import { useScrollTop } from './hooks/useScrollTop';
import './i18n';

const pageVariants = {
  initial: {
    opacity: 0,
    x: '100%'
  },
  in: {
    opacity: 1,
    x: 0
  },
  out: {
    opacity: 0,
    x: '-100%'
  }
};

const pageTransition = {
  type: 'tween',
  ease: 'anticipate',
  duration: 0.3
};

function PrivateRoute({ children }) {
  const isAuthenticated = !!localStorage.getItem('auth_token');
  return isAuthenticated ? children : <Navigate to="/login" replace />;
}

function PublicRoute({ children }) {
  const isAuthenticated = !!localStorage.getItem('auth_token');
  return isAuthenticated ? <Navigate to="/" replace /> : children;
}

function AuthenticatedLayout({ children }) {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-20">
      <TopBar showBack />
      <AnimatePresence mode="wait">
        <motion.main
          initial="initial"
          animate="in"
          exit="out"
          variants={pageVariants}
          transition={pageTransition}
          className="py-4"
        >
          <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:max-w-7xl">
            {children}
          </div>
        </motion.main>
      </AnimatePresence>
      <BottomNav />
    </div>
  );
}

function AppContent() {
  useScrollTop();
  
  return (
    <Routes>
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        } 
      />
      <Route 
        path="/reset-password" 
        element={
          <PublicRoute>
            <ResetPasswordForm />
          </PublicRoute>
        } 
      />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      
      <Route 
        path="/" 
        element={
          <PrivateRoute>
            <AuthenticatedLayout>
              <Dashboard />
            </AuthenticatedLayout>
          </PrivateRoute>
        } 
      />
      <Route 
        path="/students" 
        element={
          <PrivateRoute>
            <AuthenticatedLayout>
              <Students />
            </AuthenticatedLayout>
          </PrivateRoute>
        } 
      />
      <Route 
        path="/students/:studentId" 
        element={
          <PrivateRoute>
            <AuthenticatedLayout>
              <Students />
            </AuthenticatedLayout>
          </PrivateRoute>
        } 
      />
      <Route 
        path="/attendance" 
        element={
          <PrivateRoute>
            <AuthenticatedLayout>
              <Attendance />
            </AuthenticatedLayout>
          </PrivateRoute>
        } 
      />
      <Route 
        path="/fees" 
        element={
          <PrivateRoute>
            <AuthenticatedLayout>
              <Fees />
            </AuthenticatedLayout>
          </PrivateRoute>
        } 
      />
      <Route 
        path="/profile" 
        element={
          <PrivateRoute>
            <AuthenticatedLayout>
              <Profile />
            </AuthenticatedLayout>
          </PrivateRoute>
        } 
      />
      <Route 
        path="/contact" 
        element={
          <PrivateRoute>
            <AuthenticatedLayout>
              <Contact />
            </AuthenticatedLayout>
          </PrivateRoute>
        } 
      />
      <Route 
        path="/payments" 
        element={
          <PrivateRoute>
            <AuthenticatedLayout>
              <PaymentHistory />
            </AuthenticatedLayout>
          </PrivateRoute>
        } 
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}